<template>
    <div class="resource-category">
        <div class="resource-category__wrapper">
            <CategoryPill :key="firstCategory" :category="firstCategory" :label="getCategoryLabel(firstCategory)" />

            <div v-if="hasMore" :class="['category-pill', {selected: expanded}]">
                <button type="button" :aria-label="ariaLabel" @click="onClick">
                    <span>{{ expanded ? '-' : '+' }}{{ restCategories.length }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import {ref, computed} from 'vue';
    import type {SelectField} from '@prismicio/client';
    import type {ResourceCategoryDocument} from '@prismicio-types';
    import {useTranslation} from '#imports';
    import CategoryPill from '~/components/CategoryPill.vue';

    const emit = defineEmits<{
        expandClick: [payload: {expanded: boolean; items: Array<{category: string; label: string}>}];
    }>();

    type ResourceCategoryItem = {
        resourceCategory: ResourceCategoryDocument;
    };

    const props = defineProps<{
        resourceCategories: ResourceCategoryItem[] | undefined;
        category:
            | SelectField<
                  | 'data'
                  | 'techAndProduct'
                  | 'marketingAndCommunication'
                  | 'designAndCreation'
                  | 'businessConsultingAndStrategy'
                  | 'businessFunctions'
                  | 'industrialEngineering'
              >
            | undefined;
    }>();

    const categories = computed(() => {
        const resourceCategories = props.resourceCategories?.map((cat) => cat.resourceCategory.uid).filter((uid) => uid !== undefined);
        if (resourceCategories && resourceCategories.length > 0) {
            return resourceCategories;
        }

        return props.category && [props.category];
    });

    const firstCategory = computed(() => categories.value?.[0] as string);
    const restCategories = computed(() => categories.value?.slice(1) ?? []);
    const hasMore = computed(() => restCategories.value.length > 0);

    function getCategoryLabel(categoryUid: string) {
        const resourceCategory = props.resourceCategories?.find((cat) => cat.resourceCategory.uid === categoryUid)?.resourceCategory;
        return resourceCategory?.data?.name ?? undefined;
    }

    const {t} = useTranslation();

    const expanded = ref(false);
    const ariaLabel = computed(() =>
        expanded.value
            ? `${t('generic.action.view.less.label')} ${restCategories.value.length}`
            : `${t('generic.action.view.more.label')} ${restCategories.value.length}`,
    );

    function onClick() {
        expanded.value = !expanded.value;
        emit('expandClick', {
            expanded: expanded.value,
            items: restCategories.value.map((category) => ({
                category,
                label: getCategoryLabel(category)!,
            })),
        });
    }
</script>

<style lang="scss">
    .resource-category {
        flex: 1;
        min-width: 0;

        &__wrapper {
            display: flex;
            gap: var(--joy-core-spacing-1);
            flex-wrap: nowrap;
            align-items: center;

            .category-pill {
                min-width: 0;

                a {
                    display: block;

                    span {
                        display: block;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }
        }
    }
</style>
